import { useCallback, useContext, useState } from "react";
import { AuthContext } from "../contexts/auth-context";

export type TableUsage = {
  physical_table_name: string;
  // organization_id is the table's owning (contributor) org, which may
  // differ from the organization_id a limits-usage request was made for.
  // Two different contributor orgs can have a bucket backed by the same
  // physical_table_name, so this is required to disambiguate table_usage
  // entries - don't key or render rows on physical_table_name alone.
  organization_id: string;
  // organization_name and dataset_name come straight from the backend -
  // don't re-derive them via a local table-name lookup, since
  // physical_table_name isn't unique across orgs. organization_name is ""
  // only on a transient registry lookup failure (fall back to
  // organization_id); dataset_name is "" when the dataset backing this
  // usage record has since been deleted (fall back to just the table name).
  organization_name: string;
  dataset_name: string;
  query_count: number;
  query_limit: number;
  query_left: number;
  enabled_flag: boolean;
  // is_live is false for a row with no currently-live bucket backing it -
  // either a dataset that's since been deleted/unlived, or a row that only
  // exists because this org queried the table before its bucket disappeared.
  // Such a row is historical: it can't be queried again as things stand, so
  // it must never read as a normal actionable "OK"/"Disabled" table (see
  // tableStatus below) or drive the near-limit/limit-reached alerts.
  is_live: boolean;
};

export type SelectedTableRef = {
  organizationName?: string;
  tableName: string;
};

// Shared by LimitsUsageBanner and the Past Queries limits popup so both
// surfaces agree on what counts as "near" or "at" a table's limit - FR-20
// Task 1 AC7 mirrors the admin panel's own WARNING_THRESHOLD_OFFSET.
export const WARNING_THRESHOLD_OFFSET = 3;

export function isNearLimit(t: TableUsage): boolean {
  return (
    t.is_live &&
    t.enabled_flag &&
    t.query_count >= t.query_limit - WARNING_THRESHOLD_OFFSET
  );
}

export function isLimitReached(t: TableUsage): boolean {
  return t.is_live && t.enabled_flag && t.query_count >= t.query_limit;
}

export type StatusTone = "ok" | "warning" | "reached" | "disabled" | "inactive";

export function tableStatus(t: TableUsage): { label: string; tone: StatusTone } {
  if (!t.is_live) return { label: "Historical", tone: "inactive" };
  if (!t.enabled_flag) return { label: "Disabled", tone: "disabled" };
  if (isLimitReached(t)) return { label: "Limit reached", tone: "reached" };
  if (isNearLimit(t)) return { label: "Nearing limit", tone: "warning" };
  return { label: "OK", tone: "ok" };
}

// Shared by LimitsUsageBanner (display) and NewQuery (proactive submit-block)
// so the two never drift apart. Table name alone doesn't identify a selected
// dataset: the same physical_table_name can be contributed by more than one
// org in a federation, so org name is also required - but only when both
// sides actually have one to compare. organization_name on a table_usage row
// is "" during a transient registry lookup failure (see TableUsage above),
// and treating a blank as a mismatch would silently drop that row - hiding a
// real "limit reached" state - instead of the graceful org_id fallback the
// rest of this module already uses for display.
export function tableUsageMatchesSelection(
  t: TableUsage,
  selectedTables: SelectedTableRef[],
): boolean {
  return selectedTables.some((s) => {
    if (
      s.tableName?.trim().toLowerCase() !==
      t.physical_table_name?.trim().toLowerCase()
    ) {
      return false;
    }
    if (!s.organizationName || !t.organization_name) return true;
    return (
      s.organizationName.trim().toLowerCase() ===
      t.organization_name.trim().toLowerCase()
    );
  });
}

export type ActivationUsage = {
  activation_count: number;
  activation_limit: number;
  activation_left: number;
  enabled_connectors: string[];
};

export type QueryLimitUsage = {
  query_count: number;
  query_limit: number;
  query_left: number;
};

export type QueryTypeUsage = {
  query_type: string;
  table_usage: TableUsage[];
  activation_usage: ActivationUsage;
  query_limit_usage: QueryLimitUsage;
};

export type ObjectiveUsageResult = {
  objective: string;
  usage?: QueryTypeUsage;
  error?: boolean;
};

// useLimitsUsage wraps GET /query/limits-usage (auth-context's getLimitsUsage)
// and unwraps its usage_by_query_type array down to the single entry each
// caller here asks for by objective - the raw response always wraps results
// in that array (even for one query_type), which the original Past Queries
// popup never accounted for and so silently rendered empty sections.
export function useLimitsUsage() {
  const { getLimitsUsage } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const fetchUsage = useCallback(
    async (
      objectives: string[],
      opts: { organizationId: string; federationId: string; matchId?: string },
    ): Promise<ObjectiveUsageResult[]> => {
      setLoading(true);
      try {
        return await Promise.all(
          objectives.map(async (objective) => {
            try {
              const data = await getLimitsUsage({
                args: {
                  organizationId: opts.organizationId,
                  federationId: opts.federationId,
                  queryType: objective,
                  ...(opts.matchId ? { matchId: opts.matchId } : {}),
                },
              });
              return { objective, usage: data?.usage_by_query_type?.[0] };
            } catch (error) {
              console.error(`Error fetching limits usage for ${objective}:`, error);
              return { objective, error: true };
            }
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [getLimitsUsage],
  );

  return { fetchUsage, loading };
}

export default useLimitsUsage;
